import React, { useState, useEffect, useRef } from 'react';
import { auth, db } from '../firebase';
import { getAttribution, getFbclid } from '../utils/attribution';
import { viewContentPlanes, initiateCheckout, leerFbp, leerFbc } from '../utils/metaPixel';
import { beginCheckout } from '../utils/googleTag';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { planLimits } from '../utils/planHelpers';
import { catalogoDePlanes, STRIPE_PRICE_IDS } from '../config/catalogoDePlanes';
import { CHECKOUT_HTTP_URL } from '../config/payments';
import { precalentarCheckout } from '../utils/precalentarCheckout';
import { parsePhoneNumberFromString, getCountries, getCountryCallingCode } from 'libphonenumber-js/min';
import { normalizarTelefono, listaDePaises, PAIS_POR_DEFECTO, crearGuardadoDiferido, ESPERA_GUARDADO_MS } from '../utils/telefonoDeContacto';
import { CONTACTO_WHATSAPP_VERSION } from '../config/legal';
import { planInicial, planAlVolver, nombreInicial } from '../utils/planInicial';
import { decidirContratar } from '../utils/decidirContratar';
import { useOrganization } from '../context/OrganizationContext';
import './Contratar.css';
import { useTranslation } from 'react-i18next';

// LOS PRICE IDS NO SE COPIAN (C5, 8-oct-2026). Estaban escritos aquí a mano, en paralelo a
// `functions/config/pricingMap.js`: coincidían, y era la cuarta copia de un dato que el repo ya
// vio divergir (ver `planLimits.parity.jest.test.js`). Ahora salen del catálogo generado, que
// los deriva de `pricingMap`, y `check-shared-module-parity.js` vigila el par.

// Identidad y precio de cada plan. Las FEATURES no viven acá: las arma
// `getTranslatedFeatures()` desde las claves i18n, y es lo único que se
// renderiza (ver el `.map` de la grilla de planes).
//
// Hasta el 14-ago-2026 este array llevaba además un `features` en español
// hardcodeado que NADIE leía — dato muerto que repetía "Mensajes ilimitados"
// y "Canal Email incluido". Se retiró junto con esos claims: una copia
// muerta de un texto que se está corrigiendo es la vía más corta para que
// alguien lo reintroduzca creyendo que es la fuente.
// MISMA FUENTE QUE LA GRILLA DE LA LANDING: el catálogo generado desde
// `functions/config/planLimits.js`. El precio, el nombre y cuál es el recomendado dejan de
// decidirse aquí, para que /contratar y la landing no puedan contradecirse.
const PLAN_CARDS = catalogoDePlanes.planes.map((p) => ({
  id: p.id,
  name: p.nombre,
  price: p.precio,
  recommended: p.recomendado,
}));

const FormularioContratar = ({ user }) => {
  const [nombre, setNombre] = useState('');
  const [orgName, setOrgName] = useState('');
  // El plan con el que llegó (`?plan=` o el botón de la landing); Pro sólo si no trae ninguno.
  // Ver `utils/planInicial.js`.
  const [selectedPlan, setSelectedPlan] = useState(() => planInicial({ search: window.location.search, atribucion: getAttribution() }));
  // Teléfono (paso 1). Se guarda AL SALIR DEL CAMPO si es válido, no al enviar: ver
  // `utils/telefonoDeContacto.js`. `ultimoTelefonoGuardado` evita reescribir lo mismo.
  const [telefono, setTelefono] = useState('');
  const [paisTelefono, setPaisTelefono] = useState(PAIS_POR_DEFECTO);
  const ultimoTelefonoGuardado = useRef(null);
  // Consentimiento de contacto por WhatsApp: DESMARCADO por defecto. Cada cambio se guarda al
  // momento. `consentimientoRegistrado` = ya hay una decisión escrita (de esta visita o de una
  // anterior): mientras no la haya, el primer guardado del teléfono y el envío escriben
  // `acepta: false`, para que «no lo marcó» quede registrado y no sea una ausencia.
  const [aceptaWhatsApp, setAceptaWhatsApp] = useState(false);
  const consentimientoRegistrado = useRef(false);
  const [loading, setLoading] = useState(false);
  // Si el paso a Stripe tarda (un arranque en frío que el precalentamiento no alcanzó a cubrir),
  // a los 5 s se dice que sigue en marcha. Sin esto, 11–19 s con un botón gris y quieto se leen
  // como «se colgó» (medido el 28-sep-2026).
  const [esperaLarga, setEsperaLarga] = useState(false);
  useEffect(() => {
    if (!loading) { setEsperaLarga(false); return undefined; }
    const id = setTimeout(() => setEsperaLarga(true), 5000);
    return () => clearTimeout(id);
  }, [loading]);
  // Volver desde Stripe con «Atrás» restaura ESTA página desde la caché de página del navegador
  // (bfcache), con el estado de React tal como quedó al salir: `loading` en true y el botón gris
  // para siempre, porque `setLoading(false)` sólo corre en el `catch`. `pageshow` con `persisted`
  // es la señal de esa restauración (bloque 1 · C5).
  useEffect(() => {
    const alRestaurar = (e) => { if (e.persisted) setLoading(false); };
    window.addEventListener('pageshow', alRestaurar);
    return () => window.removeEventListener('pageshow', alRestaurar);
  }, []);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('addons');
  const { t: tc } = useTranslation('common');

  const paises = React.useMemo(() => {
    let nombres = null;
    try { nombres = new Intl.DisplayNames([i18n.language || 'es'], { type: 'region' }); } catch { nombres = null; }
    return listaDePaises(getCountries(), getCountryCallingCode, nombres ? (c) => nombres.of(c) : null);
  }, [i18n.language]);

  const getTranslatedFeatures = (planId) => {
    const limits = planLimits[planId];
    if (planId === 'starter') {
      return [
        t('contratar.features.botIncluded', { count: limits.botsIncluded }),
        t('contratar.features.numberIncluded', { count: limits.numerosIncluded }),
        t('contratar.features.usersIncluded', { count: limits.seatsIncluded }),
        t('contratar.features.noMessageMarkup'),
        t('contratar.features.socialDMs'),
        t('contratar.features.calendarOrEcommerce'),
        // C7: `seatsMax` y `numerosMax` del Starter son 2 y 1, y ni `addon_seats` ni
        // `addon_numero` están en sus `allowedAddons`. Decirlo aquí evita la pregunta.
        t('contratar.features.starterNoAmplia'),
      ];
    }
    return [
      t('contratar.features.botIncluded', { count: limits.botsIncluded }),
      t('contratar.features.numberIncluded', { count: limits.numerosIncluded }),
      t('contratar.features.usersIncluded', { count: limits.seatsIncluded }),
      t('contratar.features.noMessageMarkup'),
      t('contratar.features.socialDMs'),
      t('contratar.features.calendarAndEcommerce'),
    ];
  };

  // Pixel de Meta — `ViewContent`: la persona VIO la pantalla de planes. Ver `utils/metaPixel.js`.
  //
  // NO es `InitiateCheckout`. Hasta el 10-sep-2026 aquí se disparaba InitiateCheckout al
  // montar, y Meta contaba 14 «inicios de pago» contra 4 sesiones de checkout reales: medía
  // «abrió la pantalla», no «empezó a pagar». InitiateCheckout va ahora en `handleSubmit`,
  // cuando la sesión de Stripe existe. Tampoco hay `PageView`: se dispara en la landing y
  // aquí mezclaría prospectos con clientes bajo el mismo nombre.
  //
  // GUARDA DE MONTAJE. `src/index.js` usa `React.StrictMode`, que duplica los efectos en
  // DESARROLLO. La guarda de host de `cargarPixel` ya impide que en local salga nada, pero la
  // guarda de montaje sigue: en producción un remontaje real contaría dos vistas.
  const pixelDisparado = useRef(false);
  useEffect(() => {
    if (pixelDisparado.current) return;
    pixelDisparado.current = true;
    viewContentPlanes();
  }, []);

  // Precalentar el checkout AL MONTAR: el arranque en frío (11–19 s) ocurre mientras la persona
  // rellena los tres pasos, no después del clic. Misma guarda de montaje que el pixel: en
  // producción un remontaje no dispara dos. Ver `utils/precalentarCheckout.js`.
  const checkoutPrecalentado = useRef(false);
  useEffect(() => {
    if (checkoutPrecalentado.current) return;
    checkoutPrecalentado.current = true;
    precalentarCheckout(CHECKOUT_HTTP_URL, (u, o) => fetch(u, o));
  }, []);

  useEffect(() => {
    const cargarDatos = async () => {
      const docRef = doc(db, 'clientes', user.uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        // Nombre: lo guardado, o el displayName de Google (mejora del 6-oct-2026).
        const nombreGuardado = nombreInicial({ guardado: data.nombre, displayName: user.displayName });
        if (nombreGuardado) setNombre(nombreGuardado);
        if (data.pendingOrgName) setOrgName(data.pendingOrgName);
        // Plan: al volver, el que la persona guardó al ir al pago gana sobre el origen de la landing
        // (caso 9 de las pruebas: volvía a Pro tras elegir Starter). Ver `utils/planInicial.js`.
        setSelectedPlan(planAlVolver({ search: window.location.search, atribucion: getAttribution(), guardado: data.plan }));
        if (typeof data.telefono === 'string' && data.telefono) {
          const n = parsePhoneNumberFromString(data.telefono);
          if (n) {
            setTelefono(n.formatNational());
            if (n.country) setPaisTelefono(n.country);
          }
          ultimoTelefonoGuardado.current = data.telefono;
        }
        if (data.contactoWhatsApp && typeof data.contactoWhatsApp.acepta === 'boolean') {
          setAceptaWhatsApp(data.contactoWhatsApp.acepta);
          consentimientoRegistrado.current = true;
        }
      }
    };
    cargarDatos();
  }, [user.uid]);

  // La decisión, con el texto que se vio: versión + idioma. La fecha la pone el servidor.
  const registroDeConsentimiento = (acepta) => ({
    acepta: acepta === true,
    version: CONTACTO_WHATSAPP_VERSION,
    idioma: i18n.language || 'es',
    decididoEn: serverTimestamp(),
  });
  // Lo que se añade a una escritura mientras no haya decisión registrada: `acepta: false`.
  const consentimientoSiFalta = () => (
    consentimientoRegistrado.current ? {} : { contactoWhatsApp: registroDeConsentimiento(aceptaWhatsApp) }
  );

  const cambiarConsentimiento = async (acepta) => {
    setAceptaWhatsApp(acepta);
    try {
      await setDoc(doc(db, 'clientes', user.uid), {
        contactoWhatsApp: registroDeConsentimiento(acepta),
      }, { merge: true });
      consentimientoRegistrado.current = true;
    } catch (err) {
      console.error('[contratar] no se pudo guardar el consentimiento de WhatsApp:', err.message);
    }
  };

  // Guarda el teléfono en cuanto es válido. Con merge y SÓLO sus campos: no toca nada más del
  // documento. Un fallo aquí no bloquea nada — el envío final vuelve a escribirlo.
  const guardarTelefono = async (texto, pais) => {
    const r = normalizarTelefono(texto, pais, parsePhoneNumberFromString);
    if (r.estado === 'vacio') {
      setErrors((prev) => ({ ...prev, telefono: undefined }));
      return;
    }
    if (r.estado === 'invalido') {
      setErrors((prev) => ({ ...prev, telefono: t('contratar.phoneInvalid') }));
      return;
    }
    setErrors((prev) => ({ ...prev, telefono: undefined }));
    if (r.e164 === ultimoTelefonoGuardado.current) return;
    // Se marca ANTES de escribir: el `onBlur` y el guardado diferido pueden llegar juntos con
    // el mismo número, y el segundo no debe repetir la escritura. Si falla, se revierte.
    const anterior = ultimoTelefonoGuardado.current;
    ultimoTelefonoGuardado.current = r.e164;
    try {
      const conConsentimiento = consentimientoSiFalta();
      await setDoc(doc(db, 'clientes', user.uid), {
        telefono: r.e164,
        telefonoPais: r.pais,
        telefonoActualizadoEn: serverTimestamp(),
        ...conConsentimiento,
      }, { merge: true });
      if (conConsentimiento.contactoWhatsApp) consentimientoRegistrado.current = true;
    } catch (err) {
      ultimoTelefonoGuardado.current = anterior;
      console.error('[contratar] no se pudo guardar el teléfono:', err.message);
    }
  };

  // GUARDADO MIENTRAS SE ESCRIBE + respaldo al ocultarse la página. Ver
  // `utils/telefonoDeContacto.js` → `crearGuardadoDiferido`. Llama a `guardarTelefono`, la MISMA
  // función del `onBlur`, a través de una ref para usar siempre la versión del último render
  // (lleva el estado del check, que `consentimientoSiFalta` necesita).
  const guardarTelefonoRef = useRef(guardarTelefono);
  guardarTelefonoRef.current = guardarTelefono;
  const guardadoDiferido = useRef(null);
  useEffect(() => {
    const diferido = crearGuardadoDiferido({
      esperaMs: ESPERA_GUARDADO_MS,
      esValido: (texto, pais) => normalizarTelefono(texto, pais, parsePhoneNumberFromString).estado === 'valido',
      guardar: (texto, pais) => guardarTelefonoRef.current(texto, pais),
      setTimer: (fn, ms) => window.setTimeout(fn, ms),
      clearTimer: (id) => window.clearTimeout(id),
    });
    guardadoDiferido.current = diferido;
    const alOcultarse = () => { if (document.visibilityState === 'hidden') diferido.ahora(); };
    const alSalir = () => diferido.ahora();
    document.addEventListener('visibilitychange', alOcultarse);
    window.addEventListener('pagehide', alSalir);
    return () => {
      document.removeEventListener('visibilitychange', alOcultarse);
      window.removeEventListener('pagehide', alSalir);
      diferido.cancelar();
      guardadoDiferido.current = null;
    };
  }, []);

  const validate = () => {
    const newErrors = {};
    if (!nombre.trim()) newErrors.nombre = t('contratar.nameRequired');
    const tel = normalizarTelefono(telefono, paisTelefono, parsePhoneNumberFromString);
    if (tel.estado === 'vacio') newErrors.telefono = t('contratar.phoneRequired');
    else if (tel.estado === 'invalido') newErrors.telefono = t('contratar.phoneInvalid');
    if (!orgName.trim()) newErrors.orgName = t('contratar.orgNameRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);

    try {
      // 1. Save client data to Firestore
      // `merge: true` y campos explícitos: NO pisa nada que no esté aquí. El teléfono va igual
      // por si el guardado del `onBlur` falló; `validate()` ya garantizó que es válido.
      const tel = normalizarTelefono(telefono, paisTelefono, parsePhoneNumberFromString);
      await setDoc(doc(db, 'clientes', user.uid), {
        nombre: nombre.trim(),
        email: user.email,
        telefono: tel.e164,
        telefonoPais: tel.pais,
        ...consentimientoSiFalta(),
        plan: selectedPlan,
        pendingOrgName: orgName.trim(),
        estadoPago: 'pendiente_pago',
        fechaCreacion: new Date(),
      }, { merge: true });

      // 2. Create Stripe Checkout Session directly
      const priceId = STRIPE_PRICE_IDS[selectedPlan];
      if (!priceId) throw new Error(t('contratar.invalidPlan'));

      // Los identificadores de Meta viajan con la atribución hasta la metadata de Stripe, para
      // que la CAPI del webhook pueda mandarlos: el servidor no los tiene de otra forma.
      //   `_fbp` la escribe el pixel en `.pux.app` (medido leyendo `fbevents.js`), así que es
      //   legible desde aquí aunque el pixel de la app no haya cargado todavía.
      //   `_fbc` sólo existe si la visita trajo `fbclid`; si la cookie no está pero el
      //   `fbclid` se capturó en el arranque, `leerFbc` la reconstruye con el formato de Meta.
      // Quien llegue directo a /contratar sin pasar por un anuncio no tendrá ninguna de las
      // dos; ése tampoco es un prospecto de campaña.
      //
      // El IMPORTE ya no se anota aquí: el `Purchase` del navegador lo lee de la confirmación
      // que escribe el webhook (`clientes/{uid}.checkoutConfirmado`), que trae el importe REAL
      // cobrado — con cupón, si lo hubo — y es el mismo que manda la CAPI.
      const fbp = leerFbp();
      const fbc = leerFbc(getFbclid());

      // Sin forzar el refresco: `getIdToken()` ya renueva solo si al token le quedan < 5 min, y
      // forzarlo era una ida y vuelta a Google en el paso donde la persona espera. No hay claims
      // personalizados que refrescar (no existe `setCustomUserClaims` en el proyecto) y el
      // checkout sólo lee `uid` y `email` del token.
      const token = await auth.currentUser.getIdToken();
      const resp = await fetch(CHECKOUT_HTTP_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
        },
        body: JSON.stringify({
          uid: user.uid,
          email: user.email,
          tipo: 'subscription',
          mode: 'subscription',
          plan: selectedPlan,
          planKey: selectedPlan,
          planType: selectedPlan,
          plan_id: selectedPlan,
          priceId,
          price_id: priceId,
          items: [{ price: priceId, priceId, quantity: 1 }],
          name: nombre.trim(),
          // session_id → habilita el waitingForOrg de Dashboard (pollea la creación
          // de la org por el webhook) y excluye la ventana post-pago del onboarding
          // gate. Stripe sustituye {CHECKOUT_SESSION_ID} en el redirect de éxito.
          successUrl: window.location.origin + '/dashboard?session_id={CHECKOUT_SESSION_ID}',
          cancelUrl: window.location.origin + '/contratar',
          // Atribución capturada en el arranque (src/utils/attribution.js). Viaja
          // al backend para terminar en la metadata de la sesión de Stripe: es el
          // único punto donde el origen de la visita y el cobro quedan unidos.
          attribution: Object.assign(
            {},
            getAttribution(),
            fbp ? { pux_fbp: fbp } : {},
            fbc ? { pux_fbc: fbc } : {}
          ),
        }),
      });

      const resData = await resp.json().catch(() => ({}));

      // El servidor sabe algo que esta pantalla no vio (p. ej. la organización se activó en otra
      // pestaña): ya hay una suscripción viva. No es un error que mostrar, es otro destino.
      if (resp.status === 409 && resData?.code === 'ORGANIZACION_CON_SUSCRIPCION') {
        setLoading(false);
        navigate(resData.redirect || '/billing', { replace: true });
        return;
      }

      if (!resp.ok || !resData?.url) {
        throw new Error(resData?.error || t('contratar.checkoutError'));
      }

      if (!resData.url.startsWith('https://checkout.stripe.com/')) {
        throw new Error(t('contratar.invalidCheckoutUrl'));
      }

      // `InitiateCheckout` · AHORA, y no antes: la sesión de Stripe existe. El servidor acaba
      // de mandar su mitad por CAPI con `event_id` = `sessionId` y el importe de la sesión; el
      // navegador manda la suya con exactamente lo mismo, y Meta deduplica. Un error antes de
      // esta línea (validación, red, Stripe) NO cuenta como inicio de checkout.
      initiateCheckout({
        sessionId: resData.sessionId,
        amountTotal: resData.amountTotal,
        currency: resData.currency,
        plan: selectedPlan,
      });
      // GA4 `begin_checkout` · el mismo momento y el mismo importe que `InitiateCheckout`.
      beginCheckout({ amountTotal: resData.amountTotal, currency: resData.currency, plan: selectedPlan });

      window.location.href = resData.url;
    } catch (err) {
      console.error('Error durante contratación:', err);
      setErrors({ general: err.message || t('contratar.genericError') });
      setLoading(false);
    }
  };

  return (
    <div className="contratar-container">
      <div className="contratar-header">
        <h1>{t('contratar.title')}</h1>
        <p className="subtitle">{t('contratar.subtitle')}</p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Step 1: Info personal */}
        <div className="contratar-step">
          <div className="step-label">
            <span className="step-number">1</span>
            {t('contratar.step1Label')}
          </div>
          <h2 className="step-title">{t('contratar.step1Title')}</h2>
          <p className="step-subtitle">{t('contratar.step1Subtitle')}</p>

          <div className="contratar-card">
            <div className="form-group">
              <label className="form-label" htmlFor="nombre">{t('contratar.fullName')}</label>
              <input
                id="nombre"
                type="text"
                className={`form-input ${errors.nombre ? 'input-error' : ''}`}
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder={t('contratar.namePlaceholder')}
              />
              {errors.nombre && <p className="field-error">{errors.nombre}</p>}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                className="form-input"
                value={user.email}
                readOnly
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="telefono">{t('contratar.phoneLabel')}</label>
              <div className="telefono-fila">
                <select
                  aria-label={t('contratar.phoneCountry')}
                  className="form-input telefono-pais"
                  value={paisTelefono}
                  onChange={(e) => {
                    const pais = e.target.value;
                    setPaisTelefono(pais);
                    // lo pendiente llevaba el país anterior: se descarta antes de guardar
                    if (guardadoDiferido.current) guardadoDiferido.current.cancelar();
                    // cambiar el país cambia el número que representa lo escrito
                    if (telefono.trim()) guardarTelefono(telefono, pais);
                  }}
                >
                  {paises.map((p) => (
                    <option key={p.codigo} value={p.codigo}>{p.nombre} (+{p.prefijo})</option>
                  ))}
                </select>
                <input
                  id="telefono"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  className={`form-input telefono-numero ${errors.telefono ? 'input-error' : ''}`}
                  value={telefono}
                  onChange={(e) => {
                    setTelefono(e.target.value);
                    if (guardadoDiferido.current) guardadoDiferido.current.programar(e.target.value, paisTelefono);
                  }}
                  onBlur={() => guardarTelefono(telefono, paisTelefono)}
                  placeholder={t('contratar.phonePlaceholder')}
                />
              </div>
              {errors.telefono && <p className="field-error">{errors.telefono}</p>}
            </div>
            <div className="form-group">
              <label className="consentimiento-whatsapp">
                <input
                  type="checkbox"
                  checked={aceptaWhatsApp}
                  onChange={(e) => cambiarConsentimiento(e.target.checked)}
                />
                <span>{t('contratar.contactWhatsAppConsent')}</span>
              </label>
            </div>
          </div>
        </div>

        {/* Step 2: Organización */}
        <div className="contratar-step">
          <div className="step-label">
            <span className="step-number">2</span>
            {t('contratar.step2Label')}
          </div>
          <h2 className="step-title">{t('contratar.step2Title')}</h2>
          <p className="step-subtitle">{t('contratar.step2Subtitle')}</p>

          <div className="contratar-card">
            <div className="form-group">
              <label className="form-label" htmlFor="orgName">{t('contratar.orgName')}</label>
              <input
                id="orgName"
                type="text"
                className={`form-input ${errors.orgName ? 'input-error' : ''}`}
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder={t('contratar.orgNamePlaceholder')}
              />
              {errors.orgName && <p className="field-error">{errors.orgName}</p>}
            </div>
          </div>
        </div>

        {/* Step 3: Plan */}
        <div className="contratar-step">
          <div className="step-label">
            <span className="step-number">3</span>
            {t('contratar.step3Label')}
          </div>
          <h2 className="step-title">{t('contratar.step3Title')}</h2>
          <p className="step-subtitle">{t('contratar.step3Subtitle')}</p>

          <div className="plan-cards-grid">
            {PLAN_CARDS.map((plan) => (
              <div
                key={plan.id}
                className={`plan-option-card ${plan.id === 'pro' ? 'plan-pro' : ''} ${selectedPlan === plan.id ? 'selected' : ''}`}
                onClick={() => setSelectedPlan(plan.id)}
              >
                {plan.recommended && (
                  <span className="recommended-badge">{t('contratar.recommended')}</span>
                )}
                <div className="plan-option-header">
                  <h3 className="plan-option-name">{plan.name}</h3>
                  <div className="plan-radio">
                    <div className="plan-radio-dot" />
                  </div>
                </div>
                <div className="plan-option-price">
                  <span className="plan-price-currency">USD</span>
                  <span className="plan-price-amount">{plan.price}</span>
                  <span className="plan-price-period"> {t('contratar.perMonth')}</span>
                </div>
                <ul className="plan-features-list">
                  {getTranslatedFeatures(plan.id).map((feature, i) => (
                    <li key={i}>
                      <span className="feature-check-icon">&#10003;</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Terms & CTA */}
        <div className="contratar-cta">
          {errors.general && <p className="field-error" style={{ textAlign: 'center', marginBottom: '1rem' }}>{errors.general}</p>}

          {/* Lo que sigue es Stripe: decirlo antes del botón baja la sorpresa del salto de dominio. */}
          <p className="cta-paso-final">{t('contratar.lastStep')}</p>
          <button
            type="submit"
            className={`btn-continuar${loading ? ' is-loading' : ''}`}
            disabled={loading}
            aria-busy={loading}
          >
            {loading ? (
              <>
                <span className="btn-spinner" aria-hidden="true" />
                {t('contratar.preparingPayment')}
              </>
            ) : t('contratar.continueToPayment')}
          </button>
          {esperaLarga && (
            <p className="cta-note cta-espera" role="status" aria-live="polite">{t('contratar.preparingPaymentSlow')}</p>
          )}
          <p className="cta-note">{t('contratar.paymentNote')}</p>
          {/* Una salida para quien duda en el último paso, en vez de abandonar: la sección de
              demo de la landing (`#demo`, con el enlace de WhatsApp). Pestaña nueva para no perder
              lo rellenado. */}
          <p className="cta-note">
            <a href="https://pux.app/#demo" target="_blank" rel="noopener noreferrer">{t('contratar.questionsBookCall')}</a>
          </p>
          {/* Consentimiento legal ya se capturó, auditable y versionado, en el
              ConsentGate previo (Bloque 4). Aquí solo una referencia plana a los
              documentos canónicos — sin checkbox (era un segundo consentimiento sin
              valor probatorio, con links a las URLs .html viejas). */}
          <p className="cta-note">
            {t('contratar.legalNotice')}{' '}
            <a href="https://pux.app/terms" target="_blank" rel="noopener noreferrer">{t('contratar.termsOfService')}</a>,{' '}
            <a href="https://pux.app/privacy" target="_blank" rel="noopener noreferrer">{t('contratar.privacyPolicy')}</a>,{' '}
            <a href="https://pux.app/ai-transparency" target="_blank" rel="noopener noreferrer">{t('contratar.aiTransparency')}</a>.
          </p>
        </div>
      </form>
    </div>
  );
};

const Contratar = () => {
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const navigate = useNavigate();
  const { t: tc } = useTranslation('common');
  const { organizations, context, switchContext } = useOrganization();

  // Un DUEÑO cuya organización ya paga no contrata otra: va a Facturación, donde se cambia de
  // plan sobre la misma suscripción (bloque 1 · C3d; ver `utils/decidirContratar.js`). Un
  // invitado no es dueño y sigue. La lista ya está resuelta: `App.js` no monta rutas hasta
  // `orgsResolved`.
  const decision = user ? decidirContratar({ orgs: organizations, uid: user.uid }) : null;
  const aFacturacion = decision && decision.accion === 'facturacion' ? decision.orgId : null;
  useEffect(() => {
    if (!aFacturacion) return;
    let vivo = true;
    (async () => {
      // Facturación muestra la organización ACTIVA: si la que paga no es la abierta, se abre.
      if (context?.orgId !== aFacturacion) {
        try {
          await switchContext('organization', aFacturacion, organizations.find((o) => o.id === aFacturacion));
        } catch (err) {
          console.error('[contratar] no se pudo abrir la organización propia:', err.message);
        }
      }
      if (vivo) navigate('/billing', { replace: true });
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aFacturacion]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (usuario) => {
      if (usuario) {
        setUser(usuario);
      } else {
        navigate('/');
      }
      setLoadingUser(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  if (loadingUser || aFacturacion) return <div className="contratar-loading">{tc('loading')}</div>;
  if (!user) return null;

  return <FormularioContratar user={user} />;
};

export default Contratar;
